import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Grid, TextField, RadioGroup, FormControlLabel, Radio, Typography, Stack, Button, Alert, Slide, MenuItem, Select, InputLabel, FormControl, Divider, CircularProgress } from '@mui/material';
import { saveManagerReview, getAllReviews } from './AnnualReviewQueries';
import { getAllCorporateUsers } from '../CorporateQueries';
import DynamicFormRenderer from '../ProbationReview/DynamicFormRenderer';

const ratingOptions = ['Needs Improvement', 'Meets Expectations', 'Exceeds Expectations'];

const unwrap = (v) => (v && typeof v === 'object' && 'S' in v ? v.S : v);
const safeJSON = (v) => {
  const raw = unwrap(v);
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};
const getEmailFromLocalStorage = () => {
  const raw = localStorage.getItem('user');
  if (!raw) return '';
  try {
    const obj = JSON.parse(raw);
    return unwrap(obj.email || (obj.user && obj.user.email) || obj.user_email || obj.employee_email) || '';
  } catch { return raw.includes('@') ? raw : ''; }
};

const normalizeUsers = (users = []) => users.map(u => ({
  email: unwrap(u.email || u.employee_email) || '',
  first_name: unwrap(u.first_name) || '',
  last_name: unwrap(u.last_name) || '',
  job_title: unwrap(u.job_title) || '',
  reporting_l1: unwrap(u.reporting_l1) || unwrap(u.manager) || '',
}));

const extractReview = (data) => {
  const item = (data && data.review) || (data && data.Item) || data || null;
  if (!item) return null;
  const userRespRaw = unwrap(item.user_response);
  const user_response = safeJSON(userRespRaw) || userRespRaw || null;
  const mgrRespRaw = unwrap(item.manager_response);
  const manager_response = safeJSON(mgrRespRaw) || mgrRespRaw || null;
  const email = unwrap(item.email || item.employee_email) || '';
  const id = unwrap(item.id) || '';
  const periodStart = unwrap(item.period_start) || '';
  const periodEnd = unwrap(item.period_end) || '';
  return { id, email, user_response, manager_response, periodStart, periodEnd };
};

const listFromAll = (all) => {
  if (!all) return [];
  if (Array.isArray(all)) return all;
  if (Array.isArray(all.reviews)) return all.reviews;
  if (Array.isArray(all.Items)) return all.Items;
  if (all.body && Array.isArray(all.body.reviews)) return all.body.reviews;
  if (all.body && Array.isArray(all.body.Items)) return all.body.Items;
  return [];
};

const hasManagerResponse = (resp) => {
  if (!resp || typeof resp !== 'object') return false;
  const keys = ['accomplishments', 'developmentAreas', 'dependability', 'teamwork', 'productivity', 'organizationalCommitment', 'overallPerformance', 'summary'];
  return keys.some(k => {
    const value = resp[k];
    return value && String(value).trim() !== '' && String(value).trim() !== 'undefined';
  });
};

const ManagerReviewForm = ({ disabled = false, selectedEmployeeEmail = '', selectedEmployeeId = '', reviewsCache = [], onSelectEmployee, details, refreshCache, hideEmployeeSelector = false, formSchema, passedReviewData }) => {
  const initialInputs = useMemo(() => ({
    accomplishments: '',
    developmentAreas: '',
    dependability: '',
    teamwork: '',
    productivity: '',
    organizationalCommitment: '',
    overallPerformance: '',
    summary: '',
  }), []);

  const [inputs, setInputs] = useState(initialInputs);
  const [reports, setReports] = useState([]);
  const [employeeEmail, setEmployeeEmail] = useState(selectedEmployeeEmail || '');
  const [employeeReviewId, setEmployeeReviewId] = useState('');
  const [employeeSelfReview, setEmployeeSelfReview] = useState(null);
  const [existingManagerReview, setExistingManagerReview] = useState(null);
  const [alert, setAlert] = useState({ open: false, severity: 'success', message: '' });
  const [loading, setLoading] = useState(false);
  const [allReviews, setAllReviews] = useState(Array.isArray(reviewsCache) ? reviewsCache : []);
  const [isPeriodActive, setIsPeriodActive] = useState(true);

  const blockedByNoSelfReview = !disabled && !employeeSelfReview;

  const isFormValid = useMemo(() => {
    if (formSchema) {
      const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
      const managerSections = schema.sections?.filter(s => s.completedBy === 'manager') || [];
      for (const section of managerSections) {
        for (const field of section.fields || []) {
          if (field.required && field.fieldType !== 'heading') {
            if (!inputs[field.fieldId] || String(inputs[field.fieldId]).trim() === '') return false;
          }
        }
      }
      return true;
    }
    return (
      String(inputs.accomplishments || '').trim() !== '' &&
      String(inputs.developmentAreas || '').trim() !== '' &&
      String(inputs.dependability || '').trim() !== '' &&
      String(inputs.teamwork || '').trim() !== '' &&
      String(inputs.productivity || '').trim() !== '' &&
      String(inputs.organizationalCommitment || '').trim() !== '' &&
      String(inputs.overallPerformance || '').trim() !== '' &&
      String(inputs.summary || '').trim() !== ''
    );
  }, [inputs, formSchema]);

  useEffect(() => {
    if (!details.periodStart || !details.periodEnd) { setIsPeriodActive(true); return; }
    const now = new Date();
    setIsPeriodActive(now >= new Date(details.periodStart) && now <= new Date(details.periodEnd));
  }, [details.periodStart, details.periodEnd]);

  useEffect(() => {
    if (selectedEmployeeEmail && selectedEmployeeEmail !== employeeEmail) {
      setEmployeeEmail(selectedEmployeeEmail);
      setInputs(initialInputs);
      setEmployeeSelfReview(null);
      setExistingManagerReview(null);
    }
    if (selectedEmployeeId && selectedEmployeeId !== employeeReviewId) {
      setEmployeeReviewId(selectedEmployeeId);
    }
  }, [selectedEmployeeEmail, selectedEmployeeId, employeeEmail, employeeReviewId, initialInputs]);

  useEffect(() => {
    if (Array.isArray(reviewsCache) && reviewsCache.length) setAllReviews(reviewsCache);
  }, [reviewsCache]);

  useEffect(() => {
    const init = async () => {
      const me = getEmailFromLocalStorage();
      if (disabled) {
        setEmployeeEmail(me);
        setInputs(initialInputs);
        setEmployeeSelfReview(null);
        setExistingManagerReview(null);
        return;
      }
      try {
        const res = await getAllCorporateUsers();
        const users = normalizeUsers((res && res.users) || (res && res.Items) || res || []);
        const directReports = users.filter(u => (u.reporting_l1 || '').toLowerCase() === (me || '').toLowerCase());
        setReports(directReports);
        if (!selectedEmployeeEmail && directReports.length > 0) {
          setEmployeeEmail(directReports[0].email);
        }
      } catch (e) { setReports([]); }
    };
    init();
  }, [disabled, selectedEmployeeEmail, initialInputs]);

  useEffect(() => {
    const load = async () => {
      if (!employeeEmail || !details.periodStart) return;
      const managerEmail = getEmailFromLocalStorage();
      const draftKey = `managerAnnualDraft_${managerEmail}_${employeeEmail}_${details.periodStart}`;
      try {
        let r = null;
        if (passedReviewData) {
          r = extractReview(passedReviewData);
        } else {
          const all = await getAllReviews();
          const sourceList = listFromAll(all);
          setAllReviews(sourceList);
          const mapped = sourceList.map(extractReview).filter(Boolean);
          r = mapped.find(x =>
            (x.email || '').toLowerCase() === employeeEmail.toLowerCase() &&
            x.periodStart === details.periodStart
          ) || null;
        }

        if (r) {
          let displayUserResp = r.user_response || null;
          let displayMgrResp = r.manager_response || null;
          if (displayUserResp && typeof displayUserResp === 'object' && displayUserResp.schema && displayUserResp.responses) {
            displayUserResp = displayUserResp.responses;
          }
          if (displayMgrResp && typeof displayMgrResp === 'object' && displayMgrResp.schema && displayMgrResp.responses) {
            displayMgrResp = displayMgrResp.responses;
          }
          setEmployeeSelfReview(displayUserResp);
          setEmployeeReviewId(r.id || '');

          if (displayMgrResp && typeof displayMgrResp === 'object' && hasManagerResponse(displayMgrResp)) {
            setExistingManagerReview(displayMgrResp);
            setInputs({});
            localStorage.removeItem(draftKey);
          } else {
            setExistingManagerReview(null);
            try {
              const draft = localStorage.getItem(draftKey);
              setInputs(draft ? JSON.parse(draft) : initialInputs);
            } catch(e) { setInputs(initialInputs); }
          }
        } else {
          setEmployeeSelfReview(null);
          setExistingManagerReview(null);
          setEmployeeReviewId('');
          setInputs(initialInputs);
        }
      } catch(e) {
        console.error('Failed to load annual review for manager:', e);
        setEmployeeSelfReview(null);
        setExistingManagerReview(null);
        setEmployeeReviewId('');
        setInputs(initialInputs);
      }
    };
    load();
  }, [employeeEmail, details.periodStart, initialInputs, passedReviewData]);

  useEffect(() => {
    if (!employeeEmail || !Array.isArray(allReviews) || allReviews.length === 0) return;
    const mapped = allReviews.map(extractReview).filter(Boolean);
    const r = mapped.find(x =>
      (x.email || '').toLowerCase() === employeeEmail.toLowerCase() &&
      x.periodStart === details.periodStart
    ) || null;
    if (r) {
      let displayUserResp = r.user_response || null;
      let displayMgrResp = r.manager_response || null;
      if (displayUserResp && typeof displayUserResp === 'object' && displayUserResp.schema && displayUserResp.responses) {
        displayUserResp = displayUserResp.responses;
      }
      if (displayMgrResp && typeof displayMgrResp === 'object' && displayMgrResp.schema && displayMgrResp.responses) {
        displayMgrResp = displayMgrResp.responses;
      }
      setEmployeeSelfReview(displayUserResp);
      if (displayMgrResp && typeof displayMgrResp === 'object' && hasManagerResponse(displayMgrResp)) {
        setExistingManagerReview(displayMgrResp);
        setInputs({});
      }
    }
  }, [allReviews, employeeEmail, details.periodStart]);

  const handleChange = (e) => {
    if (disabled) return;
    setInputs(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Auto-save draft
  useEffect(() => {
    if (disabled || !employeeEmail || existingManagerReview || !isPeriodActive) return;
    const managerEmail = getEmailFromLocalStorage();
    const draftKey = `managerAnnualDraft_${managerEmail}_${employeeEmail}_${details.periodStart}`;
    const timeoutId = setTimeout(() => {
      const hasContent = Object.values(inputs).some(v => String(v || '').trim() !== '');
      if (hasContent) {
        try { localStorage.setItem(draftKey, JSON.stringify(inputs)); } catch(e) {}
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [inputs, disabled, employeeEmail, existingManagerReview, details.periodStart, isPeriodActive]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled || !employeeEmail || blockedByNoSelfReview) return;
    if (!isPeriodActive) {
      setAlert({ open: true, severity: 'error', message: 'Cannot submit review outside the active period.' });
      return;
    }
    setLoading(true);
    try {
      const managerEmail = getEmailFromLocalStorage();
      let responseData = { ...inputs };
      if (formSchema) {
        const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
        const managerSections = schema.sections?.filter(s => s.completedBy === 'manager') || [];
        responseData = { schema: managerSections, responses: inputs, submittedAt: new Date().toISOString(), submittedBy: managerEmail };
      }
      await saveManagerReview({
        responseData,
        ...details,
        managerEmail,
        employeeEmail,
        employeeId: employeeReviewId,
      });
      const draftKey = `managerAnnualDraft_${managerEmail}_${employeeEmail}_${details.periodStart}`;
      localStorage.removeItem(draftKey);
      setAlert({ open: true, severity: 'success', message: 'Manager review submitted successfully.' });
      if (refreshCache) await refreshCache();
      setExistingManagerReview({ ...inputs });
      setInputs({});
    } catch (err) {
      setAlert({ open: true, severity: 'error', message: 'Failed to submit manager review.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Employee Selector */}
      {!disabled && !hideEmployeeSelector && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="employee-select-label">Select Employee</InputLabel>
          <Select
            labelId="employee-select-label"
            label="Select Employee"
            value={employeeEmail}
            onChange={(e) => {
              setEmployeeEmail(e.target.value);
              setEmployeeReviewId('');
              setInputs(initialInputs);
              setEmployeeSelfReview(null);
              setExistingManagerReview(null);
              onSelectEmployee && onSelectEmployee(e.target.value);
            }}
            disabled={reports.length === 0}
          >
            {reports.map(r => (
              <MenuItem key={r.email} value={r.email}>
                {`${r.first_name || ''} ${r.last_name || ''}`.trim()} — {r.job_title || ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!isPeriodActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This review period is not currently active. Reviews can only be submitted during the active period.
        </Alert>
      )}

      {/* Employee Self Review Section */}
      {!disabled && (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Employee Self Review</Typography>
          <Divider sx={{ mb: 2 }} />
          {employeeSelfReview ? (
            formSchema ? (
              <DynamicFormRenderer
                formSchema={formSchema}
                formData={employeeSelfReview}
                setFormData={() => {}}
                sectionFilter="employee"
                readOnly={true}
              />
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}><Typography variant="body2"><strong>Accomplishments:</strong> {employeeSelfReview.accomplishments || '-'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2"><strong>Development Areas:</strong> {employeeSelfReview.developmentAreas || '-'}</Typography></Grid>
                <Grid item xs={12} md={4}><Typography variant="body2"><strong>Dependability:</strong> {employeeSelfReview.dependability || '-'}</Typography></Grid>
                <Grid item xs={12} md={4}><Typography variant="body2"><strong>Teamwork:</strong> {employeeSelfReview.teamwork || '-'}</Typography></Grid>
                <Grid item xs={12} md={4}><Typography variant="body2"><strong>Productivity:</strong> {employeeSelfReview.productivity || '-'}</Typography></Grid>
                <Grid item xs={12} md={4}><Typography variant="body2"><strong>Organizational Commitment:</strong> {employeeSelfReview.organizationalCommitment || '-'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2"><strong>Additional Comments:</strong> {employeeSelfReview.comments || '-'}</Typography></Grid>
              </Grid>
            )
          ) : (
            <Alert severity="warning">No self review submitted yet.</Alert>
          )}
        </Paper>
      )}

      {/* Manager Review Section */}
      {disabled ? (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Manager Comments</Typography>
          <Divider sx={{ mb: 2 }} />
          {hasManagerResponse(existingManagerReview) ? (
            formSchema ? (
              <DynamicFormRenderer formSchema={formSchema} formData={existingManagerReview} setFormData={() => {}} sectionFilter="manager" readOnly={true} />
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}><Typography variant="body2"><strong>Accomplishments:</strong> {existingManagerReview.accomplishments || '-'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2"><strong>Development Areas:</strong> {existingManagerReview.developmentAreas || '-'}</Typography></Grid>
                <Grid item xs={12} md={3}><Typography variant="body2"><strong>Dependability:</strong> {existingManagerReview.dependability || '-'}</Typography></Grid>
                <Grid item xs={12} md={3}><Typography variant="body2"><strong>Teamwork:</strong> {existingManagerReview.teamwork || '-'}</Typography></Grid>
                <Grid item xs={12} md={3}><Typography variant="body2"><strong>Productivity:</strong> {existingManagerReview.productivity || '-'}</Typography></Grid>
                <Grid item xs={12} md={3}><Typography variant="body2"><strong>Organizational Commitment:</strong> {existingManagerReview.organizationalCommitment || '-'}</Typography></Grid>
                <Grid item xs={12} md={3}><Typography variant="body2"><strong>Overall Performance:</strong> {existingManagerReview.overallPerformance || '-'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2"><strong>Summary:</strong> {existingManagerReview.summary || '-'}</Typography></Grid>
              </Grid>
            )
          ) : (
            <Alert severity="info">Your manager has not submitted comments yet.</Alert>
          )}
        </Paper>
      ) : hasManagerResponse(existingManagerReview) ? (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Manager Review</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>To be completed by the manager after reviewing the employee's self-evaluation</Typography>
          <Divider sx={{ mb: 2 }} />
          {formSchema ? (
            <DynamicFormRenderer formSchema={formSchema} formData={existingManagerReview} setFormData={() => {}} sectionFilter="manager" readOnly={true} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="body2"><strong>Accomplishments:</strong> {existingManagerReview.accomplishments || '-'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="body2"><strong>Development Areas:</strong> {existingManagerReview.developmentAreas || '-'}</Typography></Grid>
              <Grid item xs={12} md={3}><Typography variant="body2"><strong>Dependability:</strong> {existingManagerReview.dependability || '-'}</Typography></Grid>
              <Grid item xs={12} md={3}><Typography variant="body2"><strong>Teamwork:</strong> {existingManagerReview.teamwork || '-'}</Typography></Grid>
              <Grid item xs={12} md={3}><Typography variant="body2"><strong>Productivity:</strong> {existingManagerReview.productivity || '-'}</Typography></Grid>
              <Grid item xs={12} md={3}><Typography variant="body2"><strong>Organizational Commitment:</strong> {existingManagerReview.organizationalCommitment || '-'}</Typography></Grid>
              <Grid item xs={12} md={3}><Typography variant="body2"><strong>Overall Performance:</strong> {existingManagerReview.overallPerformance || '-'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="body2"><strong>Summary:</strong> {existingManagerReview.summary || '-'}</Typography></Grid>
            </Grid>
          )}
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Manager Review</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>To be completed by the manager after reviewing the employee's self-evaluation</Typography>
          <Divider sx={{ mb: 2 }} />
          {blockedByNoSelfReview && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The employee has not completed their self review yet. Manager review will be enabled after submission.
            </Alert>
          )}
          {alert.open && (
            <Slide in={alert.open} direction="down">
              <Alert severity={alert.severity} onClose={() => setAlert(a => ({ ...a, open: false }))}>{alert.message}</Alert>
            </Slide>
          )}
          {!blockedByNoSelfReview && (
            <Box component="form" onSubmit={handleSubmit}>
              {formSchema ? (
                <DynamicFormRenderer
                  formSchema={formSchema}
                  formData={inputs}
                  setFormData={setInputs}
                  sectionFilter="manager"
                  readOnly={!isPeriodActive}
                />
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField required label="What would you describe as the individual's key accomplishments during the last year?" name="accomplishments" value={inputs.accomplishments || ''} onChange={handleChange} fullWidth multiline rows={3} disabled={blockedByNoSelfReview || !isPeriodActive} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField required label="What areas of development have you identified for the individual?" name="developmentAreas" value={inputs.developmentAreas || ''} onChange={handleChange} fullWidth multiline rows={3} disabled={blockedByNoSelfReview || !isPeriodActive} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Dependability</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Reliable, steady, trustworthy.</Typography>
                    <RadioGroup name="dependability" value={inputs.dependability || ''} onChange={handleChange}>
                      {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} label={opt} />)}
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Teamwork</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Collaborative. Communicative.</Typography>
                    <RadioGroup name="teamwork" value={inputs.teamwork || ''} onChange={handleChange}>
                      {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} label={opt} />)}
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Productivity</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Highest level of abilities.</Typography>
                    <RadioGroup name="productivity" value={inputs.productivity || ''} onChange={handleChange}>
                      {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} label={opt} />)}
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Organizational Commitment & Adherence</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>How reliably did the employee follow organizational processes and standards?</Typography>
                    <RadioGroup name="organizationalCommitment" value={inputs.organizationalCommitment || ''} onChange={handleChange}>
                      {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} label={opt} />)}
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Overall Performance</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Considering experience and role.</Typography>
                    <RadioGroup name="overallPerformance" value={inputs.overallPerformance || ''} onChange={handleChange}>
                      {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} label={opt} />)}
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField required label="Summary" name="summary" value={inputs.summary || ''} onChange={handleChange} fullWidth multiline rows={3} disabled={blockedByNoSelfReview || !isPeriodActive} />
                  </Grid>
                </Grid>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button variant="outlined" disabled={loading || !isPeriodActive}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || !employeeEmail || !isFormValid || !isPeriodActive}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  Submit Manager Review
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ManagerReviewForm;
